import { default as Assignment } from '@mui/icons-material/Assignment';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import VerticalAlignBottom from '@mui/icons-material/VerticalAlignBottom';
import VerticalAlignTop from '@mui/icons-material/VerticalAlignTop';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Tooltip,
    Typography
} from '@mui/material';
import { green, lightBlue } from '@mui/material/colors';
import LabelOrdenacao from '@/components/shared/LabelOrdenacao';
import { MEIO_JULGAMENTO_PROCESSO } from '@/dominio/MeioJulgamentoProcesso';
import { action, computed, makeObservable, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import Configuracao from '../../util/Configuracao';
import ReactComponent from '../ReactComponent';
import Certidao from './Certidao';
import Impedimento from '../sessao/Impedimento';
import SituacaoAcordao from '../sessao/SituacaoAcordao';

class ModalCertidao extends ReactComponent {
    aberto = false;
    abrirCertidaoDialog = false;

    handleDialogPreCertidao = () => {
        this.abrirCertidaoDialog = !this.abrirCertidaoDialog;
    };

    handleCloseDialogPreCertidao = () => {
        this.abrirCertidaoDialog = false;
    };

    toggleAberto = () => {
        this.aberto = !this.aberto;
        this.salaSessaoStore.modalCertidaoAberto = !this.salaSessaoStore.modalCertidaoAberto;
        this.handleCloseDialogPreCertidao();
    };

    fechar = () => {
        this.aberto = false;
        this.salaSessaoStore.modalCertidaoAberto = false;
    };

    constructor(props) {
        super(props);

        makeObservable(this, {
            aberto: observable,
            abrirCertidaoDialog: observable,
            handleDialogPreCertidao: action,
            handleCloseDialogPreCertidao: action,
            toggleAberto: action,
            fechar: action,
            registro: computed,
            existePresidenteComposicao: computed,
            existeRelatorProcesso: computed,
            existeProcuradorSessao: computed,
            bloquearEdicaoCertidao: computed,
            corBotaoCertidao: computed,
            tooltipCertidao: computed,
            abrirCertidaoAnteriorCriada: action,
            abrirProximaCertidaoCriada: action,
            botaoCertidaoAnteriorDisabled: computed,
            botaoProximaCertidaoDisabled: computed,
            certidaoDesabilitada: computed,
            rascunhoCertidao: computed,
            ordemProcessos: computed,
            listaProcessos: computed
        });
    }

    get registro() {
        return this.observableProps.registro;
    }

    get existePresidenteComposicao() {
        return (
            this.registro &&
            this.registro.composicao.findIndex((composicao) => composicao.magistrado.presidenteSessao === true) !== -1
        );
    }

    validarProcuradorProcesso() {
        return (
            this.registro?.tipoJulgamento === MEIO_JULGAMENTO_PROCESSO.VIRTUAL.codigo ||
            this.registro?.tipoJulgamento === MEIO_JULGAMENTO_PROCESSO.REMETIDO_PARA_SESSAO_VINCULADA.codigo ||
            this.registro?.procurador ||
            (this.registro?.processo?.origem === Configuracao.ORIGEM_PJE && this.procuradorSessao)
        );
    }

    procuradorSessao() {
        let sessoes = this.sessaoStore.sessaoSelecionada ? this.sessaoStore.sessaoSelecionada.sessoes : [];
        return sessoes.find((sessao) => sessao.id === this.registro.sessaoId).procurador;
    }

    get existeRelatorProcesso() {
        return !!this.registro?.processo?.relator?.sigla;
    }

    get existeProcuradorSessao() {
        return !!this.registro?.procurador?.codigo;
    }

    get bloquearEdicaoCertidao() {
        return (
            this.registro.certidaoJulgamentoTransferida &&
            !this.registro.informacoesAdicionais.permitirQuebraAssinaturaCertidao
        );
    }

    get corBotaoCertidao() {
        if (this.registro.certidaoJulgamentoAssinada) {
            return green[700];
        }
        return this.registro.certidaoGerada ? lightBlue[900] : 'black';
    }

    get tooltipCertidao() {
        let procuradorValidado = this.validarProcuradorProcesso();
        if (!this.existePresidenteComposicao && this.registro.certidaoJulgamentoAssinada) {
            return 'Certidão de julgamento assinada - Defina o Presidente da composição para editar a certidão';
        }
        if (!this.existePresidenteComposicao && this.registro.certidaoGerada) {
            return 'Certidão de julgamento não assinada - Defina o Presidente da composição para editar a certidão';
        }
        if (!procuradorValidado && this.registro.certidaoJulgamentoAssinada) {
            return 'Certidão de julgamento assinada - Defina o Procurador para editar a certidão';
        }
        if (!procuradorValidado && this.registro.certidaoGerada) {
            return 'Certidão de julgamento não assinada - Defina o Procurador para editar a certidão';
        }
        if (!this.existePresidenteComposicao) {
            return 'Defina o Presidente da composição para criar/editar a certidão';
        }
        if (!procuradorValidado) {
            return 'Defina o Procurador para criar/editar a certidão';
        }
        if (!this.existeRelatorProcesso) {
            return 'Não é possível criar/editar certidão de processo sem relator';
        }
        if (this.registro.certidaoJulgamentoAssinada) {
            return 'Certidão de julgamento assinada';
        }
        return this.registro.certidaoGerada ? 'Editar certidão de julgamento' : 'Criar certidão de julgamento';
    }

    abrirCertidaoAnteriorCriada = () => {
        this.salaSessaoStore.exibirProcessosComCertidao = true;
        this.salaSessaoStore.expandirProcessoAnterior();
    };

    abrirProximaCertidaoCriada = () => {
        this.salaSessaoStore.exibirProcessosComCertidao = true;
        this.salaSessaoStore.expandirProximoProcesso();
    };

    get processos() {
        return this.salaSessaoStore.processosEmExibicao;
    }

    get botaoCertidaoAnteriorDisabled() {
        return (
            this.processos.length === 0 ||
            this.processos[0].processo.numeroCompleto === this.registro.processo.numeroCompleto
        );
    }

    get botaoProximaCertidaoDisabled() {
        return (
            this.processos.length === 0 ||
            this.processos[this.processos.length - 1].processo.numeroCompleto === this.registro.processo.numeroCompleto
        );
    }

    get certidaoDesabilitada() {
        if (this.registro.certidaoGerada) {
            return false;
        }
        return !this.existePresidenteComposicao || !this.existeRelatorProcesso || !this.validarProcuradorProcesso();
    }

    get rascunhoCertidao() {
        return this.registro.rascunhoCertidao ? this.registro.rascunhoCertidao.toString() : '';
    }

    get ordemProcessos() {
        const ordemProcessoAtual = this.listaProcessos.findIndex(
            (registro) => this.registro.processo.numeroCompleto === registro.processo.numeroCompleto
        );
        const ordemCertidaoAtual = this.processos.findIndex(
            (registro) => this.registro.processo.numeroCompleto === registro.processo.numeroCompleto
        );
        return (
            <>
                <LabelOrdenacao
                    titulo="Ordem dos processos"
                    conteudo={`${parseInt(ordemProcessoAtual) + 1} / ${this.listaProcessos.length} processos`}
                    circular={false}
                />
                <LabelOrdenacao
                    titulo="Ordem das certidões"
                    conteudo={`${parseInt(ordemCertidaoAtual) + 1} / ${this.processos.length} certidões`}
                    circular={false}
                />
            </>
        );
    }

    get listaProcessos() {
        return (
            this.store.opcoesProcessosEmExibicao
                .find((opcaoProcessos) => opcaoProcessos.id === 'A')
                .funcaoProcessosFiltrados() || []
        );
    }

    render() {
        return (
            <>
                <Tooltip
                    title={this.tooltipCertidao}
                    placement="top"
                >
                    {this.registro.certidaoGerada ? (
                        <div>
                            <IconButton
                                onClick={this.toggleAberto}
                                style={{ padding: '8px' }}
                                disabled={this.certidaoDesabilitada}
                                size="large"
                            >
                                <Assignment
                                    style={{
                                        fill: this.corBotaoCertidao,
                                        fillOpacity: this.certidaoDesabilitada ? '0.4' : '1'
                                    }}
                                />
                            </IconButton>
                        </div>
                    ) : (
                        <div>
                            <IconButton
                                onClick={
                                    !this.observableProps.registro.liberadoPeloRelator
                                        ? this.handleDialogPreCertidao
                                        : this.toggleAberto
                                }
                                style={{ padding: '8px' }}
                                disabled={this.certidaoDesabilitada}
                                size="large"
                            >
                                <Assignment
                                    style={{
                                        fill: this.corBotaoCertidao,
                                        fillOpacity: this.certidaoDesabilitada ? '0.4' : '1'
                                    }}
                                />
                            </IconButton>
                        </div>
                    )}
                </Tooltip>
                {this.abrirCertidaoDialog && (
                    <Dialog
                        open={this.abrirCertidaoDialog}
                        onClose={this.handleCloseDialogPreCertidao}
                    >
                        <DialogTitle>Criar certidão julgamento</DialogTitle>
                        <DialogContent>
                            {this.rascunhoCertidao === '' ? (
                                <b style={{ color: 'rgb(183, 28, 28)' }}>
                                    AVISO: Voto não liberado pelo relator. Confirma a criação da certidão sem decisão?
                                </b>
                            ) : (
                                <b style={{ color: 'rgb(183, 28, 28)' }}>
                                    AVISO: Voto não liberado pelo relator. Confirma a criação da certidão?
                                </b>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={this.handleCloseDialogPreCertidao}
                                color="secondary"
                                variant="outlined"
                                endIcon={<CancelIcon />}
                            >
                                Cancelar
                            </Button>
                            <div>
                                <Button
                                    onClick={this.toggleAberto}
                                    color="primary"
                                    variant="outlined"
                                    endIcon={<CheckIcon />}
                                >
                                    Confirmar
                                </Button>
                            </div>
                        </DialogActions>
                    </Dialog>
                )}
                {this.aberto && (
                    <Dialog
                        open={this.aberto}
                        fullWidth
                        maxWidth="xl"
                        PaperProps={{ style: { height: '100%' } }}
                    >
                        <DialogTitle style={{ padding: '8px 12px 5px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        gap: 1,
                                        alignItems: 'center'
                                    }}
                                >
                                    {this.ordemProcessos}
                                    <Typography
                                        style={{ fontSize: '1.1rem' }}
                                        display="inline"
                                    >
                                        Processo Nº {this.registro.processo.numeroCompleto}{' '}
                                        {this.bloquearEdicaoCertidao && (
                                            <>
                                                <Typography display="inline">- </Typography>
                                                <Typography
                                                    style={{ color: 'red' }}
                                                    display="inline"
                                                >
                                                    CERTIDÃO BLOQUEADA PARA EDIÇÃO (Transferida para o{' '}
                                                    {this.registro.processo.origem})
                                                </Typography>
                                            </>
                                        )}
                                    </Typography>
                                </Box>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        alignItems: 'center',
                                        width: '130px'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mr: 1 }}>
                                        <Impedimento
                                            registro={this.registro}
                                            isferramentaDetalhes={true}
                                        />
                                        <SituacaoAcordao registro={this.registro} />
                                    </Box>
                                    {!this.salaSessaoStore.processoModal && (
                                        <>
                                            <Tooltip
                                                title={
                                                    this.botaoCertidaoAnteriorDisabled
                                                        ? ''
                                                        : 'Abrir certidão anterior (Ctrl + ' +
                                                          String.fromCharCode(0x25b2) +
                                                          ')'
                                                }
                                            >
                                                <div>
                                                    <Button
                                                        style={{ minWidth: '24px', padding: '4px 8px' }}
                                                        onClick={this.abrirCertidaoAnteriorCriada}
                                                        disabled={this.botaoCertidaoAnteriorDisabled}
                                                    >
                                                        <VerticalAlignTop />
                                                    </Button>
                                                </div>
                                            </Tooltip>
                                            <Tooltip
                                                title={
                                                    this.botaoProximaCertidaoDisabled
                                                        ? ''
                                                        : 'Abrir próxima certidão (Ctrl + ' +
                                                          String.fromCharCode(0x25bc) +
                                                          ')'
                                                }
                                            >
                                                <div>
                                                    <Button
                                                        style={{ minWidth: '24px', padding: '4px 8px' }}
                                                        onClick={this.abrirProximaCertidaoCriada}
                                                        disabled={this.botaoProximaCertidaoDisabled}
                                                    >
                                                        <VerticalAlignBottom />
                                                    </Button>
                                                </div>
                                            </Tooltip>
                                        </>
                                    )}
                                    <Tooltip title="Fechar janela">
                                        <div>
                                            <Button
                                                color="secondary"
                                                style={{ minWidth: '24px', padding: '4px' }}
                                                onClick={this.fechar}
                                            >
                                                <Close />
                                            </Button>
                                        </div>
                                    </Tooltip>
                                </div>
                            </div>
                        </DialogTitle>
                        <Certidao
                            aberto={this.aberto}
                            registro={this.registro}
                        />
                    </Dialog>
                )}
            </>
        );
    }
}

export default inject('stores')(observer(ModalCertidao));
